import React, { useEffect, useState } from 'react';
import '../../ApplicationsDashboard/ApplicationsDashboard.css';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/Sidebar/Sidebar';
import SeedsLogo from "../../../assets/images/SeedsLogo.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faBell,
    faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import UserProfileDropdown from '../../../components/UserProfile/UserProfile';
import CustomPagination from '../../../components/Pagination/CustomPagination';
import { useDispatch, useSelector } from 'react-redux';
import { getPaymentTransactionReport } from '../../../redux/ReportsReducer/action';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { downloadReport, downloadReportByDateRange, resetFilters } from '../../../utils/reportUtils';
import DownloadReport from '../DownloadReport';
import FilterModal from '../FilterModal/FilterModal';
import FunnelIconSvg from '../../../components/SVG/FunnelIconSvg';
import { REPORTS_COMPONENTS } from '../../../config/reports';
import { isAuthorised } from '../../../utils/common';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import { getOfficers } from '../../../redux/UserReducer/action';

const PaymentTransactionReports = () => {
    let tokens = JSON.parse(sessionStorage.getItem("tokens")) || null;
    let authToken = tokens?.token || null;

    const { paymentTransactionReport, pagination, officerProfile, officers } = useSelector((store) => {
        return {
            paymentTransactionReport: store.ReportsReducer.paymentTransactionReport.data,
            pagination: store.ReportsReducer.paymentTransactionReport.pagination,
            officerProfile: store.UserReducer.officerProfile,
            officers: store.UserReducer.officers.officers
        }
    }, []);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const storedFilters = JSON.parse(sessionStorage.getItem('paymentTransactionFilters')) || {};

    const [paymentTransactionFilters, setPaymentTransactionFilters] = useState({
        customer_id: storedFilters?.customer_id || "",
        branch_code: storedFilters?.branch_code || [],
        merchant_order_id: storedFilters?.merchant_order_id || "",
        order_id: storedFilters?.order_id || "",
        transaction_status: storedFilters?.transaction_status || "",
        phonepe_transaction_id: storedFilters?.phonepe_transaction_id || "",
        phonepe_reference_id: storedFilters?.phonepe_reference_id || "",
        payment_mode: storedFilters?.payment_mode || "",
        payment_type: storedFilters?.payment_type || "",
        remark: storedFilters?.remark || "",
        completion_start_date: storedFilters?.completion_start_date || "",
        completion_end_date: storedFilters?.completion_end_date || "",
        creation_start_date: storedFilters?.creation_start_date || "",
        creation_end_date: storedFilters?.creation_end_date || "",
        global_search: storedFilters?.global_search || ""
    });

    const [currentPage, setCurrentPage] = useState(parseInt(sessionStorage.getItem('reportsCurrentPage')) || 1);
    const [limit, setLimit] = useState(parseInt(sessionStorage.getItem('reportsLimit')) || 10);
    const totalCount = parseInt(sessionStorage.getItem('reportsTotalCount')) || 0;
    const totalPages = Math.ceil(totalCount / limit);

    const [completionDateRange, setCompletionDateRange] = useState([
        paymentTransactionFilters?.completion_start_date ? new Date(paymentTransactionFilters.completion_start_date) : null,
        paymentTransactionFilters?.completion_end_date ? new Date(paymentTransactionFilters.completion_end_date) : null,
    ]);

    const [creationDateRange, setCreationDateRange] = useState([
        paymentTransactionFilters?.creation_start_date ? new Date(paymentTransactionFilters.creation_start_date) : null,
        paymentTransactionFilters?.creation_end_date ? new Date(paymentTransactionFilters.creation_end_date) : null,
    ]);

    const [creationStartDate, creationEndDate] = creationDateRange;
    const [completionStartDate, completionEndDate] = completionDateRange;

    useEffect(() => {
        dispatch(getPaymentTransactionReport(currentPage, limit, paymentTransactionFilters, null));

        sessionStorage.setItem('reportsCurrentPage', currentPage);
        sessionStorage.setItem('reportsLimit', limit);
        sessionStorage.setItem('paymentTransactionFilters', JSON.stringify(paymentTransactionFilters));
        // }, [dispatch, limit, currentPage, paymentTransactionFilters]);
    }, [dispatch, limit, currentPage]);

    useEffect(() => {
        dispatch(getOfficers(1, 1000, { is_active: true }));
    }, [dispatch]);

    useEffect(() => {
        return () => {
            sessionStorage.removeItem("paymentTransactionFilters");
            sessionStorage.removeItem("reportsCurrentPage");
            sessionStorage.removeItem("reportsLimit");
            sessionStorage.removeItem("reportsTotalPages");
            sessionStorage.removeItem("reportsTotalCount");
        };
    }, []);

    const handlePageChange = (newPage) => {
        // Ensure we're working with 1-based pagination
        const pageToSet = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(pageToSet);
        sessionStorage.setItem('reportsCurrentPage', pageToSet.toString());
    };

    const formatDate = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleDateRangeChange = (update, dateType) => {
        let updatedFilters;
        if (dateType === "creation") {
            setCreationDateRange(update);

            const [start, end] = update;

            updatedFilters = {
                ...paymentTransactionFilters,
                creation_start_date: formatDate(start),
                creation_end_date: formatDate(end),
            };
        }
        else if (dateType === "completion") {
            setCompletionDateRange(update);

            const [start, end] = update;

            updatedFilters = {
                ...paymentTransactionFilters,
                completion_start_date: formatDate(start),
                completion_end_date: formatDate(end),
            };
        }

        setPaymentTransactionFilters(updatedFilters);
        sessionStorage.setItem("paymentTransactionFilters", JSON.stringify(updatedFilters));
    };

    const handleFilterChange = (e) => {
        let { name, value } = e.target;

        if (typeof value === 'string') {
            value = value.replace(/[\t\n\r]+/g, '').replace(/^\s+/, '');
        }

        let updatedFilters = {
            ...paymentTransactionFilters,
            [name]: value
        };

        setPaymentTransactionFilters(updatedFilters);
        sessionStorage.setItem("paymentTransactionFilters", JSON.stringify(updatedFilters));
    }

    useEffect(() => {
        sessionStorage.setItem('reportsCurrentPage', currentPage);
        sessionStorage.setItem('reportsLimit', limit);
    }, [limit, currentPage]);

    // Branch options for the All Filters panel
    const branchOptions = React.useMemo(() => {
        if (!officers || !Array.isArray(officers)) return [];

        const options = officers
            .filter(branch => branch?.branch_code)
            .map(branch => ({
                label: `${branch.branch_code} - ${branch.branch_name}`,
                value: branch.branch_code
            }));

        return options.filter(
            (item, index, self) =>
                index === self.findIndex(obj => obj.value === item.value)
        );
    }, [officers]);

    return (
        <>
            <div className="reportsDashboardContainer">
                <div className="reportsDashboard">
                    <Sidebar />

                    <div className="dashboardMainContent">
                        <div className="contentHeader">
                            <div className="userLogo">
                                <img src={SeedsLogo} alt="logo" className="SeedsLogo" />
                            </div>
                            <div className="headerRightPanel">
                                {/* <div className="headerSearchBar">
                                <input type="text" placeholder="Search" />
                                <FontAwesomeIcon icon={faSearch} />
                            </div>
                            <div className="headerNotifications">
                                <FontAwesomeIcon icon={faBell} />
                                <span className="notificationBadge">9+</span>
                            </div>
                            <div className="headerHelp">
                                <FontAwesomeIcon icon={faQuestionCircle} />
                            </div> */}
                                <UserProfileDropdown />
                            </div>
                        </div>

                        <div className="pageContentWrapper">
                            <div className="pageHeaderSection">
                                <Breadcrumb items={[
                                    { label: 'Admin' },
                                    { label: 'Reports', onClick: () => navigate('/reports') },
                                    { label: 'Payment Transactions' }
                                ]} />

                                <div className="pageActions">
                                    <h2>Payment Transactions</h2>
                                </div>
                            </div>

                            <div className="searchControlSection">
                                <div className="searchGroup">
                                    {isAuthorised(
                                        officerProfile,
                                        REPORTS_COMPONENTS['payment-transaction_search']
                                    ) && <div className="tableSearchBar">
                                            <input
                                                // type="text" 
                                                type="search"
                                                placeholder="Search"
                                                name="global_search"
                                                value={paymentTransactionFilters?.global_search}
                                                onChange={handleFilterChange}
                                                onInput={(e) => {
                                                    if (e.target.value === "") {
                                                        let updatedFilters = {
                                                            ...paymentTransactionFilters,
                                                            global_search: e.target.value
                                                        }

                                                        setPaymentTransactionFilters(updatedFilters)

                                                        dispatch(getPaymentTransactionReport(1, limit, updatedFilters));
                                                    }
                                                }}
                                            />
                                            {/* <FontAwesomeIcon icon={faSearch} /> */}
                                        </div>}
                                    <div className="searchActionButtons">
                                        {isAuthorised(
                                            officerProfile,
                                            REPORTS_COMPONENTS['payment-transaction_search']
                                        ) && <button
                                            className="searchButton iconOnly"
                                            title="Search"
                                            onClick={() => {
                                                dispatch(getPaymentTransactionReport(1, limit, paymentTransactionFilters, null));
                                            }}
                                        >
                                                <FontAwesomeIcon icon={faSearch} />
                                            </button>}
                                        {isAuthorised(
                                            officerProfile,
                                            REPORTS_COMPONENTS['payment-transaction_download']
                                        ) && <button
                                            className="downloadButton"
                                            // onClick={() => downloadReportByDateRange("applications", applicationFilters?.created_from, applicationFilters?.created_to)}
                                            // onClick={() => setShowDownloadModal(true)}
                                            onClick={() => {
                                                downloadReport("payment-transaction", paymentTransactionFilters, setIsDownloading);
                                            }}
                                            disabled={isDownloading}
                                        >
                                                {isDownloading ? (
                                                    <>
                                                        <span className="buttonSpinner"></span>
                                                        Downloading...
                                                    </>
                                                ) : (
                                                    <>
                                                        Download
                                                    </>
                                                )}
                                            </button>}
                                        {isAuthorised(
                                            officerProfile,
                                            REPORTS_COMPONENTS['payment-transaction_filters']
                                        ) && <button
                                            className="filterButton"
                                            onClick={() => setShowFilterModal(true)}
                                        >
                                                <FunnelIconSvg fillColor={"black"} />
                                            </button>}
                                    </div>
                                </div>
                            </div>

                            {
                                showFilterModal && (
                                    <FilterModal
                                        reportTitle={"payment-transaction"}
                                        filters={paymentTransactionFilters}
                                        setFilters={setPaymentTransactionFilters}
                                        dynamicOptions={{ branch_code: branchOptions }}
                                        resetFilters={() => {
                                            let resettedFilters = resetFilters(paymentTransactionFilters);

                                            setPaymentTransactionFilters(resettedFilters);
                                            dispatch(getPaymentTransactionReport(1, limit, resettedFilters, null));
                                            setShowFilterModal(false);
                                            sessionStorage.setItem("paymentTransactionFilters", JSON.stringify(resettedFilters));
                                            return resettedFilters;
                                        }}
                                        handleApplyFilters={(finalFilters) => {
                                            handlePageChange(1);
                                            dispatch(getPaymentTransactionReport(1, limit, finalFilters, null));
                                            setShowFilterModal(false);
                                        }}
                                        onClose={() => {
                                            setShowFilterModal(false)
                                        }}
                                    />
                                )
                            }

                            <div className="tableCard">
                                <main className="">
                                    <div className="reportsTableContainer">
                                        <table className="applications-table" style={{ overflowX: "auto" }}>
                                            <thead>
                                                <tr>
                                                    <th>Customer ID</th>
                                                    <th>Branch</th>
                                                    <th>Phonepe Reference ID</th>
                                                    <th>Transaction Completion Date</th>
                                                    <th>Sum of Transaction Amount</th>
                                                    <th>Sum of Total Adjusted Principal</th>
                                                    <th>Sum of Total Adjusted Interest</th>
                                                    <th>Entity Type</th>
                                                    <th>Payment Status</th>
                                                </tr>
                                                {/* <tr className="filter-row">
                                        <td></td>
                                        <td>
                                            <input
                                                type="text"
                                                name="merchant_order_id"
                                                value={paymentTransactionFilters?.merchant_order_id || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                name="order_id"
                                                value={paymentTransactionFilters?.order_id || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td>
                                            <select
                                                name="transaction_status"
                                                value={paymentTransactionFilters?.transaction_status || ""}
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            >
                                                <option value="">All</option>
                                                <option value="COMPLETED">Completed</option>
                                                <option value="FAILED">Failed</option>
                                                <option value="PENDING">Pending</option>
                                                <option value="EXPIRED_VIA_SYSTEM">Expired via system</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                name="phonepe_transaction_id"
                                                value={paymentTransactionFilters?.phonepe_transaction_id || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                name="phonepe_reference_id"
                                                value={paymentTransactionFilters?.phonepe_reference_id || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td>
                                            <select
                                                name="payment_mode"
                                                value={paymentTransactionFilters?.payment_mode || ""}
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            >
                                                <option value="">All</option>
                                                <option value="UPI">UPI</option>
                                                <option value="CARD">Card</option>
                                                <option value="NET_BANKING">Net Banking</option>
                                                <option value="WALLET">Wallet</option>
                                            </select>
                                        </td>
                                        <td>
                                            <select
                                                name="payment_type"
                                                value={paymentTransactionFilters?.payment_type || ""}
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            >
                                                <option value="">All</option>
                                                <option value="TRANCHE_PAYMENT">Tranche Payment</option>
                                                <option value="SANCTION_CHARGE">Sanction Charge</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                name="remark"
                                                value={paymentTransactionFilters?.remark || ""}
                                                placeholder="Search"
                                                className="column-filter"
                                                onChange={handleFilterChange}
                                            />
                                        </td>
                                        <td>
                                            <DatePicker
                                                selectsRange
                                                startDate={completionStartDate}
                                                endDate={completionEndDate}
                                                onChange={(dates) => handleDateRangeChange(dates, "completion")}
                                                isClearable
                                                placeholderText="Select date range"
                                                className="form-control form-control-sm date-picker-filter"
                                            />
                                        </td> 
                                        <td>
                                            <DatePicker
                                                selectsRange
                                                startDate={creationStartDate}
                                                endDate={creationEndDate}
                                                onChange={(dates) => handleDateRangeChange(dates, "creation")}
                                                isClearable
                                                placeholderText="Select date range"
                                                className="form-control form-control-sm date-picker-filter"
                                            />
                                        </td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr> */}
                                            </thead>
                                            <tbody>
                                                {
                                                    paymentTransactionReport?.length === 0 ?
                                                        <tr>
                                                            <td colSpan={12} className="no-data-cell">
                                                                No records found
                                                            </td>
                                                        </tr> :
                                                        paymentTransactionReport?.map((transaction, index) => (
                                                            <tr key={index}>
                                                                <td>{transaction?.customer_id}</td>
                                                                <td>{transaction?.branch_code ? `${transaction.branch_code} - ${transaction?.branch_name || ''}` : ''}</td>
                                                                <td>{transaction?.phonepe_reference_id}</td>
                                                                <td>{transaction?.transaction_completion_date}</td>
                                                                <td>{transaction?.['Sum of transaction_amount']}</td>
                                                                <td>{transaction?.['Sum of total_adjusted_principal']}</td>
                                                                <td>{transaction?.['Sum of total_adjusted_interest']}</td>
                                                                <td>{transaction?.entity_type}</td>
                                                                <td>{transaction?.payment_status}</td>
                                                            </tr>
                                                        ))
                                                }
                                            </tbody>
                                        </table>
                                    </div>

                                    {paymentTransactionReport?.length > 0 && <div className="paginationControls">
                                        <CustomPagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>}
                                </main>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {
                showDownloadModal && (
                    <DownloadReport
                        isOpen={showDownloadModal}
                        onClose={() => setShowDownloadModal(false)}
                        reportTitle={"payment-transaction"}
                    />
                )
            }
        </>
    )
}

export default PaymentTransactionReports